import React, { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  LinearProgress,
  Card,
  CardContent,
  IconButton,
  Chip,
  Alert,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  AudioFile,
  VideoFile,
  CheckCircle,
  Error,
  Info,
  Close,
  SmartToy,
  HelpOutline,
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';

import { useContentUpload } from '../hooks/useContent';
import { useAnalyticsStats } from '../hooks/useAnalytics';
import { UPLOAD_CONFIG, validateFileType, validateFileSize, formatFileSize as apiFormatFileSize } from '../api';

interface UploadFile {
  id: string;
  file: File;
  progress: number;
  status: 'uploading' | 'processing' | 'completed' | 'error';
  error?: string;
  contentId?: string;
  metadata?: {
    title?: string;
    artist?: string;
    album?: string;
    duration?: string;
    bitrate?: string;
  };
}

const Upload: React.FC = () => {
  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [defaultSettings, setDefaultSettings] = useState({
    category: 'background',
    quality: 'high',
    tags: '',
    isAiGenerated: false,
    registerToBucket: false,
  });

  // Hooks
  const uploadMutation = useContentUpload();
  const { data: stats, refetch: refetchStats } = useAnalyticsStats();

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleFiles = useCallback(async (files: File[]) => {
    // Validate files
    const validFiles = files.filter(file => {
      // Check file type
      if (!validateFileType(file, UPLOAD_CONFIG.allowedTypes) && 
          !UPLOAD_CONFIG.allowedExtensions.some(ext => 
            file.name.toLowerCase().endsWith(ext))) {
        toast.error(`Invalid file type: ${file.name}`);
        return false;
      }

      // Check file size
      if (!validateFileSize(file, UPLOAD_CONFIG.maxFileSize)) {
        toast.error(`File too large: ${file.name} (max ${apiFormatFileSize(UPLOAD_CONFIG.maxFileSize)})`);
        return false;
      }

      return true;
    });

    if (validFiles.length === 0) return;

    // Create upload file objects
    const newUploadFiles: UploadFile[] = validFiles.map(file => ({
      id: Math.random().toString(36).substring(7),
      file,
      progress: 0,
      status: 'uploading',
    }));

    setUploadFiles(prev => [...prev, ...newUploadFiles]);

    // Start uploads
    for (const uploadFile of newUploadFiles) {
      startUploadFile(uploadFile);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSettings.category, defaultSettings.tags]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  }, [handleFiles]);

  const startUploadFile = async (uploadFile: UploadFile) => {
    try {
      const uploadRequest = {
        file: uploadFile.file,
        category: defaultSettings.category,
        tags: defaultSettings.tags.split(',').map(tag => tag.trim()).filter(Boolean),
        is_ai_generated: defaultSettings.isAiGenerated,
        register_to_bucket: defaultSettings.registerToBucket,
      };

      const response = await uploadMutation.mutateAsync({
        uploadRequest,
        onProgress: (progress) => {
          setUploadFiles(prev => 
            prev.map(f => 
              f.id === uploadFile.id 
                ? { ...f, progress: progress.percentage }
                : f
            )
          );
        },
      });

      // Upload successful
      setUploadFiles(prev => 
        prev.map(f => 
          f.id === uploadFile.id 
            ? { 
                ...f, 
                status: 'completed', 
                progress: 100,
                contentId: response.content_id,
                metadata: {
                  title: uploadFile.file.name.replace(/\.[^/.]+$/, ''),
                  artist: 'Processing...',
                },
              }
            : f
        )
      );

      // Refresh stats
      await refetchStats();

    } catch (error: any) {
      setUploadFiles(prev => 
        prev.map(f => 
          f.id === uploadFile.id 
            ? { 
                ...f, 
                status: 'error', 
                error: error.message || 'Upload failed',
              }
            : f
        )
      );
    }
  };

  const removeFile = (fileId: string) => {
    setUploadFiles(prev => prev.filter(file => file.id !== fileId));
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle sx={{ color: '#10b981' }} />;
      case 'error': return <Error sx={{ color: '#ef4444' }} />;
      case 'processing': return <Info sx={{ color: '#4a9eff' }} />;
      default: return <CloudUpload sx={{ color: '#ff6b35' }} />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'uploading': return 'Uploading...';
      case 'processing': return 'Processing...';
      case 'completed': return 'Upload Complete';
      case 'error': return 'Upload Failed';
      default: return 'Unknown';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
        Upload Content
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              File Upload
            </Typography>
            
            <Box
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              sx={{
                border: `2px dashed ${dragActive ? '#4a9eff' : '#333'}`,
                borderRadius: 2,
                p: 6,
                textAlign: 'center',
                bgcolor: dragActive ? 'rgba(74, 158, 255, 0.05)' : 'transparent',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: '#4a9eff',
                  bgcolor: 'rgba(74, 158, 255, 0.02)',
                },
              }}
            >
              <CloudUpload sx={{ fontSize: 48, color: '#4a9eff', mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1 }}>
                Drag and drop audio or video files here
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                or click to browse files
              </Typography>
              <Button
                variant="contained"
                component="label"
                startIcon={<CloudUpload />}
              >
                Choose Files
                <input
                  type="file"
                  hidden
                  multiple
                  accept="audio/*,video/*,.mp3,.wav,.flac,.aac,.ogg,.mp4,.mov,.webm,.mkv"
                  onChange={(e) => {
                    if (e.target.files) {
                      handleFiles(Array.from(e.target.files));
                    }
                  }}
                />
              </Button>
            </Box>

            <Alert severity="info" sx={{ mt: 2 }}>
              Supported formats: MP3, WAV, FLAC, AAC, OGG, MP4, MOV, WebM. Maximum file size: 2GB per file.
            </Alert>
          </Paper>

          {uploadFiles.length > 0 && (
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Upload Progress ({uploadFiles.length} files)
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setUploadFiles([])}
                  startIcon={<Delete />}
                >
                  Clear All
                </Button>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {uploadFiles.map(uploadFile => {
                  const isVideo = uploadFile.file.type.startsWith('video/');
                  return (
                  <Card key={uploadFile.id} variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        {isVideo ? (
                          <VideoFile sx={{ mr: 2, mt: 0.5, color: '#ff6b35' }} />
                        ) : (
                          <AudioFile sx={{ mr: 2, mt: 0.5, color: '#4a9eff' }} />
                        )}
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                            {uploadFile.file.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {formatFileSize(uploadFile.file.size)} • {uploadFile.file.type || (isVideo ? 'Video file' : 'Audio file')}
                          </Typography>
                          
                          {uploadFile.metadata && (
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                              {uploadFile.metadata.artist && (
                                <Chip label={uploadFile.metadata.artist} size="small" />
                              )}
                              {uploadFile.metadata.duration && (
                                <Chip label={uploadFile.metadata.duration} size="small" />
                              )}
                              {uploadFile.metadata.bitrate && (
                                <Chip label={uploadFile.metadata.bitrate} size="small" />
                              )}
                            </Box>
                          )}

                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            {getStatusIcon(uploadFile.status)}
                            <Typography variant="body2" sx={{ ml: 1, mr: 2 }}>
                              {getStatusText(uploadFile.status)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {Math.round(uploadFile.progress)}%
                            </Typography>
                          </Box>
                          
                          <LinearProgress
                            variant="determinate"
                            value={uploadFile.progress}
                            sx={{
                              '& .MuiLinearProgress-bar': {
                                bgcolor: uploadFile.status === 'completed' ? '#10b981' : '#4a9eff',
                              },
                            }}
                          />
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => removeFile(uploadFile.id)}
                          sx={{ ml: 1 }}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                  );
                })}
              </Box>
            </Paper>
          )}
        </Grid>

        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Upload Settings
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Default Category</InputLabel>
                <Select
                  value={defaultSettings.category}
                  label="Default Category"
                  onChange={(e) => setDefaultSettings(prev => ({ ...prev, category: e.target.value }))}
                >
                  <MenuItem value="background">Background Music</MenuItem>
                  <MenuItem value="jingle">Jingles</MenuItem>
                  <MenuItem value="announcement">Announcements</MenuItem>
                  <MenuItem value="commercial">Commercial</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Quality Preset</InputLabel>
                <Select
                  value={defaultSettings.quality}
                  label="Quality Preset"
                  onChange={(e) => setDefaultSettings(prev => ({ ...prev, quality: e.target.value }))}
                >
                  <MenuItem value="high">High Quality (320kbps)</MenuItem>
                  <MenuItem value="medium">Medium Quality (192kbps)</MenuItem>
                  <MenuItem value="low">Low Quality (128kbps)</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Default Tags"
                value={defaultSettings.tags}
                onChange={(e) => setDefaultSettings(prev => ({ ...prev, tags: e.target.value }))}
                placeholder="music, background, corporate"
                helperText="Comma-separated tags"
              />

              <Divider sx={{ my: 1 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={defaultSettings.isAiGenerated}
                      onChange={(e) => setDefaultSettings(prev => ({
                        ...prev,
                        isAiGenerated: e.target.checked,
                        registerToBucket: e.target.checked ? true : prev.registerToBucket,
                      }))}
                      icon={<SmartToy />}
                      checkedIcon={<SmartToy sx={{ color: '#10b981' }} />}
                    />
                  }
                  label="AI-Generated Content"
                />
                <Tooltip title="Check this for AI-generated music. Skips commercial database lookup and registers to your private audio library for future identification.">
                  <HelpOutline fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </Box>

              {defaultSettings.isAiGenerated && (
                <Alert severity="info" sx={{ py: 0.5 }}>
                  AI content will be registered to your private audio fingerprint library for future identification.
                </Alert>
              )}

              <Divider sx={{ my: 1 }} />

              <Typography variant="body2" color="text.secondary">
                Auto-processing enabled
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Metadata extraction
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Duplicate detection
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Quality analysis
              </Typography>
            </Box>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Upload Statistics
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Total Uploads Today</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {stats?.upload_count_today || 0}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Success Rate</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#10b981' }}>
                  {stats?.success_rate ? `${stats.success_rate.toFixed(1)}%` : 'N/A'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Processing Queue</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {stats?.processing_queue || 0} items
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Storage Used</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {stats ? formatFileSize(stats.storage_used) : 'N/A'}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Upload;